// v50.122 — Crypto wallet React context.
//
// Holds the unlocked company DEK in memory for the lifetime of a session.
// Surfaces (DisputesPage, eventually InboxPage) call useCryptoWallet() to
// either:
//   • Read .dek + .isUnlocked to encrypt/decrypt
//   • Render <CryptoLockedGate> to prompt for passphrase when locked
//   • Call setupWallet(passphrase) on first use to provision the DEK
//
// Never persists keys. On signout (lc-auth-changed → user becomes null),
// the DEK is dropped from state. New session means re-derive from passphrase.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useShellUser } from "@/features/auth/AuthContext";
import { supabase } from "@/lib/api/supabase-client";
import {
  base64ToBytes,
  bytesToBase64,
  setupNewCompanyDek,
  unlockExistingWallet,
} from "@/lib/crypto/wallet";

interface WalletState {
  /* True when a DEK is loaded in memory. */
  isUnlocked:  boolean;
  /* True when we've checked the DB and found no wrapped DEK for this user.
     UI uses this to render the "create wallet" flow instead of "unlock". */
  needsSetup:  boolean | null;
  /* The unlocked company DEK — only available when isUnlocked === true.
     Treat as opaque; callers should pass it to lib/crypto/wallet.ts helpers. */
  dek:         CryptoKey | null;
  /* Last error from setup/unlock attempts. */
  lastError:   string | null;
}

interface WalletApi extends WalletState {
  unlock(passphrase: string): Promise<boolean>;
  setupWallet(passphrase: string): Promise<boolean>;
  lock(): void;
}

const Ctx = createContext<WalletApi | null>(null);

export function CryptoWalletProvider({ children }: { children: React.ReactNode }) {
  const user = useShellUser();
  const [state, setState] = useState<WalletState>({
    isUnlocked: false,
    needsSetup: null,
    dek:        null,
    lastError:  null,
  });

  /* On user / company change: drop any held DEK + check whether a wrapped
     row exists for this user-company pair. The needsSetup boolean drives
     the "create wallet" vs "unlock wallet" branch in the gate UI. */
  useEffect(() => {
    if (!user?.id || !user?.companyId) {
      setState({ isUnlocked: false, needsSetup: null, dek: null, lastError: null });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("crypto_user_keys")
        .select("company_id")
        .eq("company_id", user.companyId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn("[crypto wallet] could not check setup state:", error);
        setState(s => ({ ...s, needsSetup: null, lastError: error.message }));
        return;
      }
      setState(s => ({ ...s, needsSetup: !data, lastError: null }));
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.companyId]);

  const unlock = useCallback(async (passphrase: string): Promise<boolean> => {
    if (!user?.id || !user?.companyId) return false;
    setState(s => ({ ...s, lastError: null }));
    try {
      const { data, error } = await supabase
        .from("crypto_user_keys")
        .select("wrapped_dek, wrap_nonce, kdf_salt, kdf_iters")
        .eq("company_id", user.companyId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) throw new Error("No wallet exists yet for this account — set one up first.");
      const { dek } = await unlockExistingWallet({
        passphrase,
        salt:       base64ToBytes(data.kdf_salt as unknown as string),
        iterations: data.kdf_iters as number,
        wrappedDek: base64ToBytes(data.wrapped_dek as unknown as string),
        wrapNonce:  base64ToBytes(data.wrap_nonce  as unknown as string),
      });
      setState(s => ({ ...s, isUnlocked: true, needsSetup: false, dek, lastError: null }));
      return true;
    } catch (e) {
      setState(s => ({ ...s, lastError: (e as Error).message }));
      return false;
    }
  }, [user?.id, user?.companyId]);

  const setupWallet = useCallback(async (passphrase: string): Promise<boolean> => {
    if (!user?.id || !user?.companyId) return false;
    setState(s => ({ ...s, lastError: null }));
    try {
      const { salt, iterations, dek, wrappedDek } = await setupNewCompanyDek(passphrase);
      const { error } = await supabase
        .from("crypto_user_keys")
        .insert({
          company_id:  user.companyId,
          user_id:     user.id,
          wrapped_dek: bytesToBase64(wrappedDek.wrapped),
          wrap_nonce:  bytesToBase64(wrappedDek.nonce),
          kdf_salt:    bytesToBase64(salt),
          kdf_iters:   iterations,
        });
      if (error) throw new Error(error.message);
      setState(s => ({ ...s, isUnlocked: true, needsSetup: false, dek, lastError: null }));
      return true;
    } catch (e) {
      setState(s => ({ ...s, lastError: (e as Error).message }));
      return false;
    }
  }, [user?.id, user?.companyId]);

  const lock = useCallback(() => {
    setState(s => ({ ...s, isUnlocked: false, dek: null }));
  }, []);

  /* Drop key on signout. The auth hook fires lc-auth-changed; we also
     react to user becoming null via the effect above. Belt-and-braces. */
  useEffect(() => {
    const onAuthChange = () => {
      if (!user?.id) lock();
    };
    window.addEventListener("lc-auth-changed", onAuthChange);
    return () => window.removeEventListener("lc-auth-changed", onAuthChange);
  }, [user?.id, lock]);

  return <Ctx.Provider value={{ ...state, unlock, setupWallet, lock }}>{children}</Ctx.Provider>;
}

export function useCryptoWallet(): WalletApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCryptoWallet must be used inside <CryptoWalletProvider>");
  return ctx;
}
